import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";

const TAB_HINTS = {
  appuntamenti: "La gestione quotidiana: sempre visibile",
  account: "Password e sicurezza: sempre visibile",
  impostazioni: "Configurazione del pannello: sempre visibile",
};

export default function PanelConfigTab({ flash, reloadPanel }) {
  const [config, setConfig] = useState(null);
  const [pages, setPages] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .get("/admin/panel-config")
      .then(({ data }) => {
        setConfig(data.tabs || []);
        setPages(data.pages || []);
      })
      .catch((e) => flash(apiErrorDetail(e), false));
  };

  useEffect(() => {
    load();
  }, []);

  const saveTabs = async () => {
    setBusy(true);
    try {
      await api.put("/admin/panel-config", { tabs: config });
      flash("Impostazioni del pannello salvate");
      reloadPanel();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setBusy(false);
    }
  };

  const savePage = async () => {
    setBusy(true);
    try {
      if (form.pid) {
        await api.put(`/admin/admin-pages/${form.pid}`, form);
        flash("Pagina del pannello aggiornata");
      } else {
        await api.post("/admin/admin-pages", form);
        flash("Pagina creata: ora è una nuova scheda del pannello");
      }
      setForm(null);
      load();
      reloadPanel();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-10">
      <div>
        <div className="mb-5">
          <h2 className="font-display text-2xl lg:text-3xl">Schede del pannello</h2>
          <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">
            Rinomina le schede a piacere e nascondi quelle che non usi. Richieste, Promemoria e Storico sono
            sottoschede della scheda Appuntamenti: rinominarle o nasconderle agisce sulle sottoschede dentro
            Appuntamenti. Le impostazioni restano salvate anche dopo la pubblicazione del sito.
          </p>
        </div>
        <div className="bg-white rounded-[24px] shadow-soft p-6 space-y-4" data-testid="panel-tabs-editor">
          {(config || []).map((t, i) => (
            <div key={t.key} className="flex flex-wrap items-center gap-4" data-testid={`panel-tab-row-${t.key}`}>
              <input
                type="text"
                value={t.label}
                disabled={t.locked}
                onChange={(e) => {
                  const next = [...config];
                  next[i] = { ...t, label: e.target.value };
                  setConfig(next);
                }}
                data-testid={`panel-tab-label-${t.key}`}
                className={`flex-1 min-w-[150px] rounded-2xl border border-taupe/40 px-4 py-3 sm:py-2.5 text-sm ${t.locked ? "opacity-60" : "bg-transparent"}`}
              />
              <span className="text-[11px] uppercase tracking-widest2 text-charcoal/40">{t.key}</span>
              <label className="ml-auto flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={t.visible}
                  disabled={t.locked}
                  onChange={(e) => {
                    const next = [...config];
                    next[i] = { ...t, visible: e.target.checked };
                    setConfig(next);
                  }}
                  data-testid={`panel-tab-visible-${t.key}`}
                  className="w-4 h-4 accent-[#6B5C50]"
                />
                {t.locked ? (TAB_HINTS[t.key] || "Sempre visibile") : "Visibile"}
              </label>
            </div>
          ))}
          <button
            onClick={saveTabs}
            disabled={busy || !config}
            data-testid="panel-tabs-save"
            className="rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors disabled:opacity-40"
          >
            Salva schede
          </button>
        </div>
      </div>

      <div className="border-t border-taupe/20 pt-8">
        <div className="mb-5">
          <h2 className="font-display text-2xl lg:text-3xl">Pagine personalizzate del pannello</h2>
          <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">
            Crea nuove schede per ciò che ti serve nello studio: note sui pazienti, turni, scorte, promemoria.
            Ogni pagina diventa una scheda del pannello, modificabile anche dopo la pubblicazione.
          </p>
        </div>

        {form && (
          <div className="bg-white rounded-[24px] shadow-soft p-6 space-y-4 mb-5" data-testid="panel-page-form">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="block">
                <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Titolo della scheda</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="panel-page-title"
                  className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm focus:border-deeptaupe focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Ordine</span>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
                  data-testid="panel-page-order"
                  className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm"
                />
              </label>
              <label className="flex items-end gap-2 text-sm pb-3">
                <input
                  type="checkbox"
                  checked={form.visible}
                  onChange={(e) => setForm({ ...form, visible: e.target.checked })}
                  data-testid="panel-page-visible"
                  className="w-4 h-4 accent-[#6B5C50]"
                />
                Visibile nel pannello
              </label>
            </div>
            <label className="block">
              <span className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Contenuto</span>
              <textarea
                rows={7}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                data-testid="panel-page-content"
                className="w-full rounded-2xl border border-taupe/40 bg-transparent px-4 py-3 text-sm focus:border-deeptaupe focus:outline-none"
              />
            </label>
            <div className="flex gap-3">
              <button
                onClick={savePage}
                disabled={busy || !form.title}
                data-testid="panel-page-save"
                className="rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors disabled:opacity-40"
              >
                Salva pagina
              </button>
              <button
                onClick={() => setForm(null)}
                data-testid="panel-page-cancel"
                className="rounded-full border border-taupe/40 text-charcoal/60 text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-peach"
              >
                Annulla
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => setForm({ pid: null, title: "", content: "", visible: true, order: pages.length + 1 })}
          data-testid="panel-page-new"
          className="inline-flex items-center gap-2 rounded-full border border-deeptaupe/40 text-deeptaupe text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 hover:bg-peach"
        >
          <Plus size={14} /> Nuova pagina del pannello
        </button>

        {pages.length === 0 && (
          <p className="text-sm text-charcoal/50 mt-4" data-testid="panel-pages-empty">
            Nessuna pagina personalizzata: creane una con "Nuova pagina del pannello".
          </p>
        )}
        <div className="space-y-3 mt-4">
          {pages.map((p) => (
            <div key={p.pid} className="flex flex-wrap items-center gap-3 bg-white rounded-2xl px-5 py-4" data-testid={`panel-page-row-${p.pid}`}>
              <span className="font-medium text-sm">{p.title}</span>
              {!p.visible && <span className="text-[11px] uppercase tracking-widest2 text-charcoal/40">Nascosta</span>}
              <span className="ml-auto flex gap-3">
                <button
                  onClick={() => setForm({ ...p })}
                  data-testid={`panel-page-edit-${p.pid}`}
                  className="text-[12px] uppercase tracking-widest2 text-charcoal/60 hover:text-deeptaupe"
                >
                  Modifica
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm(`Eliminare la pagina "${p.title}" dal pannello?`)) {
                      try {
                        await api.delete(`/admin/admin-pages/${p.pid}`);
                        load();
                        reloadPanel();
                        flash("Pagina rimossa dal pannello");
                      } catch (e) {
                        flash(apiErrorDetail(e), false);
                      }
                    }
                  }}
                  data-testid={`panel-page-delete-${p.pid}`}
                  aria-label="Elimina pagina"
                  className="text-red-500/70 hover:text-red-700"
                >
                  <Trash2 size={15} />
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
